import type { SVGProps } from 'react';

/**
 * Iconos de trazo, 24×24, grosor 1,7 — la misma familia que usa Polaris.
 * Se dibujan con `currentColor` para que hereden el color del contexto.
 */
type Props = SVGProps<SVGSVGElement> & { size?: number };

function Base({ size = 20, children, ...resto }: Props) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...resto}
    >
      {children}
    </svg>
  );
}

export const Correo = (p: Props) => (
  <Base {...p}>
    <rect x="2.5" y="4.5" width="19" height="15" rx="2.5" />
    <path d="m3.5 6.5 8.5 6 8.5-6" />
  </Base>
);

export const Candado = (p: Props) => (
  <Base {...p}>
    <rect x="4" y="10.5" width="16" height="10" rx="2.5" />
    <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" />
  </Base>
);

export const Ojo = (p: Props) => (
  <Base {...p}>
    <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
    <circle cx="12" cy="12" r="3" />
  </Base>
);

export const OjoTachado = (p: Props) => (
  <Base {...p}>
    <path d="M9.9 5.8A9.6 9.6 0 0 1 12 5.5c6 0 9.5 6.5 9.5 6.5a17 17 0 0 1-2.9 3.8" />
    <path d="M6.4 7.9A17 17 0 0 0 2.5 12S6 18.5 12 18.5a9.5 9.5 0 0 0 3.6-.7" />
    <path d="m9.9 9.9a3 3 0 0 0 4.2 4.2M3 3l18 18" />
  </Base>
);

export const Reloj = (p: Props) => (
  <Base {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 7v5.4l3.6 2.2" />
  </Base>
);

export const Grafico = (p: Props) => (
  <Base {...p}>
    <path d="M4 20V4M4 20h16" />
    <path d="M8 20v-6M13 20V9M18 20v-9" />
  </Base>
);

export const Escudo = (p: Props) => (
  <Base {...p}>
    <path d="M12 3l7.5 3v6c0 4.4-3.1 8.2-7.5 9.5C7.6 20.2 4.5 16.4 4.5 12V6Z" />
    <path d="m9 12 2.2 2.2L15.5 10" />
  </Base>
);

export const Flecha = (p: Props) => (
  <Base {...p}>
    <path d="M19 12H5m6-7-7 7 7 7" />
  </Base>
);

export const Usuario = (p: Props) => (
  <Base {...p}>
    <circle cx="12" cy="8.5" r="3.8" />
    <path d="M4.5 20.5a7.5 7.5 0 0 1 15 0" />
  </Base>
);

export const Llave = (p: Props) => (
  <Base {...p}>
    <circle cx="8" cy="12" r="4" />
    <path d="M12 12h9M18 12v3.5M15.5 12v2.5" />
  </Base>
);

export const Check = (p: Props) => (
  <Base {...p}>
    <path d="m5 12.5 4.5 4.5L19 7.5" />
  </Base>
);

export const Equis = (p: Props) => (
  <Base {...p}>
    <path d="M6 6l12 12M18 6 6 18" />
  </Base>
);

export const Buscar = (p: Props) => (
  <Base {...p}>
    <circle cx="11" cy="11" r="6.5" />
    <path d="m16 16 4.5 4.5" />
  </Base>
);

export const Panel = (p: Props) => (
  <Base {...p}>
    <rect x="3.5" y="3.5" width="7" height="7" rx="1.5" />
    <rect x="13.5" y="3.5" width="7" height="7" rx="1.5" />
    <rect x="3.5" y="13.5" width="7" height="7" rx="1.5" />
    <rect x="13.5" y="13.5" width="7" height="7" rx="1.5" />
  </Base>
);

export const Edificio = (p: Props) => (
  <Base {...p}>
    <path d="M4 21V5.5A1.5 1.5 0 0 1 5.5 4h7A1.5 1.5 0 0 1 14 5.5V21" />
    <path d="M14 10h4.5A1.5 1.5 0 0 1 20 11.5V21M2.5 21h19" />
    <path d="M7 8h4M7 12h4M7 16h4M17 14h1M17 17.5h1" />
  </Base>
);

export const Personas = (p: Props) => (
  <Base {...p}>
    <circle cx="9" cy="8" r="3.3" />
    <path d="M2.5 20a6.5 6.5 0 0 1 13 0" />
    <path d="M16 5.4a3.3 3.3 0 0 1 0 5.2M17.5 14.2a6.5 6.5 0 0 1 4 5.8" />
  </Base>
);

export const Brujula = (p: Props) => (
  <Base {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="m15.5 8.5-2 5-5 2 2-5z" />
  </Base>
);

export const Lista = (p: Props) => (
  <Base {...p}>
    <path d="M5 3.5h14a1.5 1.5 0 0 1 1.5 1.5v14a1.5 1.5 0 0 1-1.5 1.5H5A1.5 1.5 0 0 1 3.5 19V5A1.5 1.5 0 0 1 5 3.5Z" />
    <path d="M7.5 8.5h2M7.5 12h2M7.5 15.5h2M12.5 8.5h4M12.5 12h4M12.5 15.5h4" />
  </Base>
);

export const Enviar = (p: Props) => (
  <Base {...p}>
    <path d="M21 3 10.5 13.5M21 3l-6.8 18-3.7-7.5L3 9.8z" />
  </Base>
);

export const Ajustes = (p: Props) => (
  <Base {...p}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-1.8-.3 1.6 1.6 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1A1.6 1.6 0 0 0 9 19.4a1.6 1.6 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0 .3-1.8 1.6 1.6 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1A1.6 1.6 0 0 0 4.6 9a1.6 1.6 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 1.8.3H9a1.6 1.6 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.6 1.6 0 0 0 1 1.5 1.6 1.6 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0-.3 1.8V9a1.6 1.6 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.6 1.6 0 0 0-1.5 1Z" />
  </Base>
);

export const Chevrones = (p: Props) => (
  <Base {...p}>
    <path d="m11 17-5-5 5-5M18 17l-5-5 5-5" />
  </Base>
);

export const Salir = (p: Props) => (
  <Base {...p}>
    <path d="M9 21H5.5A1.5 1.5 0 0 1 4 19.5v-15A1.5 1.5 0 0 1 5.5 3H9" />
    <path d="M16 16.5 20.5 12 16 7.5M20 12H9" />
  </Base>
);

export const Menu = (p: Props) => (
  <Base {...p}>
    <path d="M4 7h16M4 12h16M4 17h16" />
  </Base>
);

/** Embudo. Para el botón de filtros dentro del buscador. */
export const Filtro = (p: Props) => (
  <Base {...p}>
    <path d="M3 5h18l-7 8v5.5l-4 2V13z" />
  </Base>
);
